import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environment/environment';
import { IActivity } from '@shared/interfaces/activity.interface';
import { IAPIArrayData } from '@shared/interfaces/api.interface';
import { IFilter } from '@shared/interfaces/filter.interface';
import { IFiltersActivity } from '@shared/interfaces/filters/filters.activity.interface';
import { IPaginable } from '@shared/interfaces/paginable.interface';
import { ISort } from '@shared/interfaces/sort.interface';
import { Activity } from '@shared/models/activity.model';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ActivitiesService implements IPaginable {
  private readonly ENDPOINT_V1: string = String(
    `${environment.ENDPOINTS.BACK_END.V1}/activities`
  );

  // When true, the backend skips the COUNT(*) query and returns a `has_more`
  // boolean instead of total_pages/total_entries. Used by dashboard widgets
  // that don't render pagination controls or a total count.
  private _liteMode = false;

  constructor(private readonly _http: HttpClient) {}

  public setLiteMode(lite: boolean): void {
    this._liteMode = lite;
  }

  public get(params: any = {}): Observable<IActivity[]> {
    return this._http
      .get<IAPIArrayData>(this.ENDPOINT_V1, {
        params
      })
      .pipe(map((res: IAPIArrayData) => Activity.fromAPIArray(res)));
  }

  public getPage(
    page: number,
    onGetRawData?: (data: IAPIArrayData) => void,
    filter?: IFilter,
    _sort?: ISort,
    dates?: IFilter[]
  ): Observable<IActivity[]> {
    let params: HttpParams = new HttpParams();
    params = params.set('page[number]', String(page));

    if (this._liteMode) {
      params = params.set('pagination', 'lite');
    }

    if (!!filter && !!filter['key']) {
      params = params.set(`filter[${filter['key']}]`, filter['value']);
    }

    // IFiltersActivity can carry a `limit` hint. The backend reads filter[limit]
    // and uses it to bound the activity scope and skip pagination — used by the
    // standalone /activities page to fetch more than the default 10 per request.
    const activityFilter = filter as IFiltersActivity | undefined;
    if (activityFilter?.limit) {
      params = params.set('filter[limit]', String(activityFilter.limit));
    }

    if (dates) {
      dates.map((f: IFilter, index: number) => {
        if (!!f && !!f['value'] && index < 2) {
          params = params.set(`dates[${f['key']}]`, f['value']);
        } else if (!!f && !!f['value'] && index === 2) {
          // HACK SINGLE FILTER
          // TO DO REFACTOR FROM SETFILTER TO SET FILTERS
          params = params.set(`filter[${f['key']}]`, f['value']);
        }
      });
    }

    return this._http
      .get<IAPIArrayData>(this.ENDPOINT_V1, { params })
      .pipe(
        tap((data: IAPIArrayData) => onGetRawData && onGetRawData(data)),
        map((res: IAPIArrayData) => Activity.fromAPIArray(res))
      );
  }
}
